import { formatNationalPhoneNumber } from "@/lib/phone"
import { EmptyPage, Plus, Trash } from "iconoir-react"
import React, { useState } from "react"

export interface FieldSettings {
  name: string
  description?: string
  description_short?: string
  slug?: string
  html_type: string
  required?: boolean
  placeholder?: any
  html_id: string
  default_text?: string
  default_checked?: boolean
  options?: string[]
  options_display?: string[]
  disabled?: boolean
  example?: string
  value?: string
  min?: number
  max?: number
  step?: number
  removable?: boolean
}

interface FormProps {
  fields: FieldSettings[]
  values?: any
  children?: React.ReactNode
  allowRemovableFields?: boolean
  sectionTitle?: string | null
  showAddParameter?: boolean
}

export default function CallForm({
  fields,
  values,
  children,
  allowRemovableFields = false,
  sectionTitle = "Settings",
  showAddParameter = true
}: FormProps): React.JSX.Element {
  const initialValues = values || {}
  const [hiddenFields, setHiddenFields] = useState<Record<string, boolean>>({})
  const [showAddField, setShowAddField] = useState(false)
  const [fieldsToAdd, setFieldsToAdd] = useState<FieldSettings[]>([])

  if (fields.length === 0) {
    return (
      // <CallCard>
      <div className="flex flex-col gap-4 items-center justify-center min-h-[100px] w-full">
        <EmptyPage width={32} height={32} className="text-gray-400" />
        {sectionTitle && <p className="text-sm text-gray-400">No {sectionTitle.toLowerCase()} found.</p>}
      </div>
      // </CallCard>
    )
  }

  // Filter out fields that should be hidden by default
  const visibleFields = fields.filter((field) => !hiddenFields[field.slug || field.html_id])

  const handleHideField = (fieldId: string) => {
    setHiddenFields((prev) => ({
      ...prev,
      [fieldId]: true
    }))
  }

  const handleAddFieldClick = () => {
    setShowAddField(!showAddField)
    setFieldsToAdd(
      fields.filter((field) => {
        const fieldId = field.slug || field.html_id
        return hiddenFields[fieldId] && field.removable
      })
    )
  }

  const handleAddField = (fieldId: string) => {
    setHiddenFields((prev) => ({
      ...prev,
      [fieldId]: false
    }))
    setShowAddField(false)
  }

  return (
    // <CallCard>
    <div className="flex flex-col gap-0 w-full">
      {sectionTitle && (
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex flex-row justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">{sectionTitle}</h3>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {visibleFields.map((field) => {
          const fieldId = field.slug || field.html_id

          return (
            <div key={fieldId} className="flex flex-col gap-2 w-full">
              {field.html_type !== "hidden" && field.html_type !== "checkbox" && (
                <div className="flex justify-between items-start">
                  <div className="w-fit flex flex-col gap-1">
                    <label htmlFor={fieldId} className="block text-base font-semibold text-gray-800">
                      {field.name}
                      {field.required && !field.disabled && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {field.description && (
                      <div className="text-sm text-gray-500 mt-1 text-wrap break-words">{field.description}</div>
                    )}
                  </div>

                  {field.removable && allowRemovableFields && (
                    <div className="w-fit flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => handleHideField(fieldId)}
                        className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-500 rounded-full hover:bg-red-200 hover:text-red-600 transition-colors"
                        title="Remove field"
                      >
                        <Trash width={22} height={22} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {field.html_type === "checkbox" && (
                <div className="flex items-center justify-between">
                  <label htmlFor={fieldId} className="text-base font-semibold text-gray-800 cursor-pointer">
                    {field.name}
                    {field.required && !field.disabled && <span className="text-red-500 ml-1">*</span>}
                    {field.description && <div className="text-sm text-gray-500 mt-1">{field.description}</div>}
                  </label>

                  <div className="flex items-center gap-4">
                    {field.removable && allowRemovableFields && (
                      <button
                        type="button"
                        onClick={() => handleHideField(fieldId)}
                        className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-500 rounded-full hover:bg-red-200 hover:text-red-600 transition-colors"
                        title="Remove field"
                      >
                        <Trash width={22} height={22} />
                      </button>
                    )}

                    {field.html_type === "checkbox" && (
                      <>
                        <div className="flex flex-row w-max grow justify-end items-center">
                          <input
                            type={field.html_type}
                            id={fieldId}
                            disabled={field.disabled === true}
                            defaultChecked={
                              initialValues[fieldId] != null ? initialValues[fieldId] : field.default_checked
                            }
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded-md focus:ring-blue-500 justify-self-end"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {field.html_type === "hidden" && <input type={field.html_type} id={fieldId} value={field.value} />}
              {(field.html_type === "text" || !field.html_type) && (
                <input
                  type={field.html_type}
                  id={fieldId}
                  className="py-3 px-4 block w-full border-gray-300 bg-white rounded-lg text-base focus:border-indigo-500 focus:ring-indigo-500 shadow-sm"
                  placeholder={field.placeholder || field.example || ""}
                  defaultValue={initialValues[fieldId] != null ? initialValues[fieldId] : field.default_text}
                  disabled={field.disabled === true}
                  required={field.required && !hiddenFields[fieldId]}
                />
              )}
              {field.html_type == "tel" && (
                <input
                  type={field.html_type}
                  id={fieldId}
                  className="py-3 px-4 block w-full border-gray-300 bg-white rounded-lg text-base focus:border-indigo-500 focus:ring-indigo-500 shadow-sm"
                  placeholder={field.placeholder}
                  pattern="^\\(?\\d{1,3}\\)?([\\s\\-]?\\d{1,4}){1,3}$"
                  defaultValue={
                    initialValues[fieldId] != null
                      ? formatNationalPhoneNumber(initialValues.country_code, initialValues[fieldId])
                      : formatNationalPhoneNumber(initialValues.country_code, field.default_text || "")
                  }
                  disabled={field.disabled === true}
                  required={field.required && !hiddenFields[fieldId]}
                />
              )}
              {field.html_type === "textarea" && (
                <textarea
                  id={fieldId}
                  className="py-3 px-4 block w-full border-gray-300 bg-white rounded-lg text-base focus:border-indigo-500 focus:ring-indigo-500 shadow-sm"
                  placeholder={field.placeholder || field.example || ""}
                  defaultValue={initialValues[fieldId] != null ? initialValues[fieldId] : field.default_text}
                  disabled={field.disabled === true}
                  required={field.required && !hiddenFields[fieldId]}
                />
              )}
              {field.html_type === "select" && (
                <select
                  id={fieldId}
                  className="py-3 px-4 block w-full border-gray-300 bg-white rounded-lg text-base focus:border-indigo-500 focus:ring-indigo-500 shadow-sm"
                  disabled={field.disabled === true}
                  defaultValue={initialValues[fieldId] != null ? initialValues[fieldId] : field.default_text}
                  required={field.required && !hiddenFields[fieldId]}
                >
                  {field?.options &&
                    field.options.map((option, index) => (
                      <option key={option} value={option}>
                        {field?.options_display
                          ? field.options_display[index]
                          : option.charAt(0).toUpperCase() + option.slice(1)}
                      </option>
                    ))}
                </select>
              )}
              {field.html_type === "number" && (
                <select
                  id={fieldId}
                  className="py-3 px-4 block w-full border-gray-300 bg-white rounded-lg text-base focus:border-indigo-500 focus:ring-indigo-500 shadow-sm"
                  disabled={field.disabled === true}
                  defaultValue={initialValues[fieldId] != null ? initialValues[fieldId] : field.default_text}
                  required={field.required && !hiddenFields[fieldId]}
                >
                  {Array.from({ length: (field.max! - field.min!) / field.step! + 1 }, (_, i) => (
                    <option key={field.min! + i * field.step!} value={field.min! + i * field.step!}>
                      {field.min! + i * field.step!}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )
        })}
        {children}
      </div>
      {allowRemovableFields &&
        showAddParameter &&
        fields.some((field) => field.removable && hiddenFields[field.slug || field.html_id]) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleAddFieldClick}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors font-medium"
            >
              <Plus width={18} height={18} />
              <span>Add a parameter</span>
            </button>
            {showAddField && fieldsToAdd.length > 0 && (
              <div className="flex flex-col gap-3 p-4 border border-gray-200 bg-gray-50 rounded-lg mt-3">
                <p className="text-base text-gray-800 font-semibold">Choose a parameter to add:</p>
                <div className="flex flex-wrap gap-2">
                  {fieldsToAdd.map((field) => {
                    const fieldId = field.slug || field.html_id
                    return (
                      <button
                        key={fieldId}
                        type="button"
                        onClick={() => handleAddField(fieldId)}
                        className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-100 hover:border-gray-400 font-medium transition-colors"
                      >
                        {field.name}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
    </div>
    // </CallCard>
  )
}
